(() => {
  if (customElements.get('rdb-currency')) return;

  class RdbCurrency extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('keydown', this.handleKeydown, options);
      this.addEventListener('click', this.handleClick, options);
      this.addEventListener('focusout', this.handleFocusout, options);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
      this.language = this.closest('.rdb-market-controls')?.querySelector('.rdb-localization');
      this.language?.addEventListener('toggle', () => { if (this.language.open) this.close(); }, options);
      document.addEventListener('pointerdown', event => {
        if (this.language?.open && !this.language.contains(event.target)) this.language.open = false;
      }, options);
      this.language?.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.language.contains(event.relatedTarget)) this.language.open = false;
      }, options);
      this.currencyNames = new Map([...this.querySelector('template[data-rdb-currency-names]')?.content.querySelectorAll('[data-code]') || []].map(item => [item.dataset.code, item.textContent.trim()]));
      this.refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.focusTimer);
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      this.button = this.querySelector('.currency-switcher');
      this.panel = this.querySelector('.futureblink-currency-dropdown');
      if (!this.button || !this.panel) return;
      const selected = this.button.textContent.trim();
      this.button.setAttribute('role', 'button');
      this.button.tabIndex = 0;
      this.button.setAttribute('aria-label', `${this.dataset.label}: ${selected}`);
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', String(this.isOpen()));
      this.panel.id = `RdbCurrency-${this.dataset.sectionId}`;
      this.button.setAttribute('aria-controls', this.panel.id);
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-label', this.dataset.displayLabel);
      this.panel.setAttribute('aria-hidden', String(!this.isOpen()));
      this.options = [...this.panel.querySelectorAll('.option')];
      this.options.forEach(option => {
        const code = option.textContent.trim();
        const name = this.currencyNames.get(code);
        if (name) {
          option.dataset.currencyName = name;
          option.setAttribute('aria-label', `${name} (${code})`);
        }
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(option.textContent.trim() === selected));
        option.tabIndex = -1;
      });
      if (!this.panel.querySelector('.rdb-currency__notice')) {
        const notice = document.createElement('p');
        notice.className = 'rdb-currency__notice';
        notice.id = `${this.panel.id}-notice`;
        notice.setAttribute('role', 'presentation');
        notice.textContent = this.dataset.estimatedNotice;
        this.panel.append(notice);
        this.button.setAttribute('aria-describedby', notice.id);
      }
    }

    isOpen() {
      return this.panel?.style.opacity === '1';
    }

    close() {
      if (this.isOpen()) this.button.click();
    }

    handleClick = event => {
      if (event.target.closest('.currency-switcher')) {
        this.closest('.rdb-market-controls')?.querySelectorAll('details[open]').forEach(details => { details.open = false; });
      }
      this.schedule();
    };

    handleFocusout = () => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    };

    handleKeydown = event => {
      if (!this.button || !this.options?.length) return;
      const option = event.target.closest('.option');
      const onButton = event.target === this.button;
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.button.focus();
      } else if (['Enter', ' '].includes(event.key) && (onButton || option)) {
        event.preventDefault();
        (option || this.button).click();
        if (option) this.button.focus();
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) && (onButton || option)) {
        event.preventDefault();
        if (!this.isOpen()) this.button.click();
        const index = this.options.indexOf(option);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.options.length - 1 : onButton ? (event.key === 'ArrowDown' ? 0 : this.options.length - 1) :
          (index + (event.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
        this.options[next].focus();
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && option) {
        const start = this.options.indexOf(option) + 1;
        const ordered = [...this.options.slice(start), ...this.options.slice(0, start)];
        const match = ordered.find(item => item.textContent.trim().startsWith(event.key.toUpperCase()));
        if (match) { event.preventDefault(); match.focus(); }
      }
    };
  }

  customElements.define('rdb-currency', RdbCurrency);
})();

(() => {
  if (window.rdbCurrencyPrices) return;
  const currencies = new Set(['AED', 'EUR', 'USD', 'GBP', 'CAD', 'AUD', 'CHF', 'SAR', 'QAR', 'KWD', 'OMR', 'BHD', 'INR', 'PKR', 'TRY', 'UAH']);
  const precisionCurrencies = new Set(['KWD', 'BHD', 'OMR']);
  const budgetSelector = '[data-rdb-budget-text], [data-rdb-budget-aria], [data-rdb-budget-currency], [data-rdb-budget-field]';
  const targetSelector = `[data-rdb-price], .money[current_price][data-processed="true"], ${budgetSelector}`;
  const pending = new Set();
  const formatters = new Map();
  const budgetStates = new WeakMap();
  const budgetFields = new WeakMap();
  const budgetForms = new WeakSet();
  const budgetDecoder = document.createElement('textarea');
  let frame = 0;
  let scanAll = true;
  let previousCurrency = '';

  function currency() {
    const selected = document.querySelector('rdb-currency .currency-switcher')?.textContent.trim();
    return currencies.has(selected) ? selected : 'AED';
  }

  function number(amount, code, trim) {
    const digits = precisionCurrencies.has(code) ? 3 : 2;
    const format = window.futureBlink_currencies?.[code]?.format || 'amount';
    const locale = format.includes('comma_separator') ? 'de-DE' : format.includes('apostrophe') ? 'de-CH' : 'en-AE';
    const key = `${locale}:${digits}:${trim}`;
    if (!formatters.has(key)) formatters.set(key, new Intl.NumberFormat(locale, {
      minimumFractionDigits: trim ? 0 : digits, maximumFractionDigits: digits
    }));
    return formatters.get(key).format(amount);
  }

  function setText(element, value) {
    if (!element || element.textContent === value) return;
    element.textContent = value;
    element.dir = 'ltr';
    element.style.unicodeBidi = 'isolate';
  }

  function quote(minor, base, code = currency()) {
    minor = Number(minor);
    if (base !== 'AED' || !Number.isSafeInteger(minor) || minor < 0) return null;
    const rates = window.futureBlink_currencyRates;
    const validRate = Number.isFinite(rates?.[base]) && rates[base] > 0 && Number.isFinite(rates?.[code]) && rates[code] > 0;
    const target = code === base || validRate ? code : base;
    const major = minor / 100;
    const amount = target === base ? major : major * rates[base] / rates[target];
    return Number.isFinite(amount) ? { amount, currency: target } : null;
  }

  function minorValue(value) {
    if (value === null || value === '') return null;
    if (!/^\d+(?:\.\d{1,2})?$/.test(String(value))) return NaN;
    const minor = Math.round(Number(value) * 100);
    return Number.isSafeInteger(minor) ? minor : NaN;
  }

  function budgetPattern(value) {
    return String(value || '').replace(/&(?:#(?:x[\da-f]+|\d+)|[a-z][a-z\d]+);/gi, entity => {
      budgetDecoder.innerHTML = entity;
      return budgetDecoder.value;
    });
  }

  function budgetData(element) {
    const data = element.dataset;
    const value = name => data[name] === undefined ? null : Number(data[name]);
    let min = value('rdbBudgetMin'), max = value('rdbBudgetMax'), amount = value('rdbBudgetAmount');
    let pattern = data.rdbBudgetText, aria = data.rdbBudgetAria;
    if (pattern === 'legacy') {
      const owner = element.closest('[data-rdb-budget-legacy]');
      if (!owner || !data.rdbBudgetUrl) return null;
      let url;
      try { url = new URL(data.rdbBudgetUrl, location.origin); } catch { return null; }
      if (url.origin !== location.origin) return null;
      min = minorValue(url.searchParams.get('filter.v.price.gte'));
      max = minorValue(url.searchParams.get('filter.v.price.lte'));
      if ([min, max].some(v => v !== null && !Number.isSafeInteger(v))) return null;
      if (max !== null && max % 100 === 99) max += 1;
      const kind = min !== null && max !== null ? 'Range' : max !== null ? 'Under' : min !== null ? 'From' : '';
      if (!kind) return null;
      amount = kind === 'Under' ? max : min;
      pattern = owner.dataset[`rdbBudget${data.rdbBudgetPurpose === 'link' ? 'Shop' : 'Label'}${kind}`];
    }
    if ([min, max, amount].some(v => v !== null && (!Number.isSafeInteger(v) || v < 0))) return null;
    return { min, max, amount, pattern: budgetPattern(pattern), aria: budgetPattern(aria), base: data.rdbBaseCurrency || 'AED' };
  }

  function renderBudgetText(element, code) {
    const data = budgetData(element);
    if (!data) return;
    const resolved = quote(0, data.base, code);
    if (!resolved) return;
    let state = budgetStates.get(element);
    if (!state) {
      state = { nodes: [...element.childNodes].map(n => n.cloneNode(true)), aria: element.getAttribute('aria-label'), signature: null };
      budgetStates.set(element, state);
    }
    const values = { __CURRENCY__: resolved.currency };
    for (const [name, minor] of [['__MIN__', data.min], ['__MAX__', data.max], ['__AMOUNT__', data.amount]]) {
      if (minor !== null) values[name] = number(quote(minor, data.base, code).amount, resolved.currency, true);
    }
    const original = resolved.currency === data.base;
    const signature = JSON.stringify([original, data.pattern, data.aria, values]);
    if (state.signature === signature) return;
    state.signature = signature;
    element.toggleAttribute('data-rdb-budget-converted', !original);
    if (original) {
      if (element.hasAttribute('data-rdb-budget-text') || element.hasAttribute('data-rdb-budget-currency')) element.replaceChildren(...state.nodes.map(n => n.cloneNode(true)));
      if (element.hasAttribute('data-rdb-budget-aria')) {
        if (state.aria === null) element.removeAttribute('aria-label'); else element.setAttribute('aria-label', state.aria);
      }
      return;
    }
    const interpolate = template => template.replace(/__(?:CURRENCY|MIN|MAX|AMOUNT)__/g, token => values[token] ?? token);
    if (data.aria) element.setAttribute('aria-label', interpolate(data.aria));
    if (element.hasAttribute('data-rdb-budget-currency')) { setText(element, resolved.currency); return; }
    if (!element.hasAttribute('data-rdb-budget-text')) return;
    const fragment = document.createDocumentFragment();
    if (element.hasAttribute('data-rdb-budget-range') && data.min !== null && data.max !== null) {
      for (const text of [values.__MIN__, `– ${values.__MAX__}`]) {
        const part = document.createElement('bdi'); part.dir = 'ltr'; part.textContent = text;
        fragment.append(part);
      }
    } else {
      for (const part of data.pattern.split(/(__(?:CURRENCY|MIN|MAX|AMOUNT)__\+?)/g)) {
        const token = part.endsWith('+') ? part.slice(0, -1) : part;
        if (Object.hasOwn(values, token)) {
          const bdi = document.createElement('bdi'); bdi.dir = 'ltr'; bdi.textContent = values[token] + (part.endsWith('+') ? '+' : '');
          fragment.append(bdi);
        } else fragment.append(document.createTextNode(part));
      }
    }
    element.replaceChildren(fragment);
  }

  function captureBudgetInput(field, state) {
    const input = state.input;
    const wasBad = state.bad;
    const changed = input.value !== state.lastValue;
    const value = input.value === '' ? null : input.valueAsNumber;
    const minor = value === null ? null : Math.round(value / state.rate * 100);
    state.bad = input.validity.badInput || input.validity.stepMismatch || (value !== null && (!Number.isFinite(value) || value < 0 || !Number.isSafeInteger(minor)));
    input.setCustomValidity(state.bad ? budgetPattern(field.dataset.rdbBudgetError) : '');
    if (!state.bad && (changed || wasBad || value === null)) state.minor = minor;
    state.lastValue = input.value;
  }

  function bindBudgetField(field) {
    let state = budgetFields.get(field);
    if (state) return state;
    const input = field.querySelector('input[type="number"]');
    if (!input) return null;
    const initial = minorValue(input.value), defaultMinor = minorValue(input.defaultValue);
    if ([initial, defaultMinor].some(v => v !== null && !Number.isSafeInteger(v))) return null;
    state = { input, minor: initial, defaultMinor, lastValue: input.value, currency: 'AED', rate: 1, bad: false, ready: false };
    budgetFields.set(field, state);
    input.addEventListener('input', () => { captureBudgetInput(field, state); pending.add(field); schedule(); });
    const form = input.form;
    if (form && !budgetForms.has(form)) {
      budgetForms.add(form);
      form.addEventListener('formdata', event => {
        for (const owner of form.querySelectorAll('[data-rdb-budget-field]')) {
          const current = bindBudgetField(owner);
          if (!current) continue;
          captureBudgetInput(owner, current);
          if (current.bad || current.minor === null) event.formData.delete(current.input.name);
          else event.formData.set(current.input.name, (current.minor / 100).toFixed(2));
        }
      });
      form.addEventListener('reset', event => queueMicrotask(() => {
        if (event.defaultPrevented) return;
        for (const owner of form.querySelectorAll('[data-rdb-budget-field]')) {
          const current = bindBudgetField(owner);
          if (!current) continue;
          Object.assign(current, { minor: current.defaultMinor, lastValue: current.input.value, currency: 'AED', rate: 1, ready: false, bad: false });
          current.input.setCustomValidity(''); pending.add(owner);
        }
        schedule();
      }));
    }
    return state;
  }

  function renderBudgetField(field, code) {
    const state = bindBudgetField(field);
    if (!state) return;
    captureBudgetInput(field, state);
    if (state.bad) return;
    const base = field.dataset.rdbBaseCurrency || 'AED', unit = quote(10000, base, code);
    if (!unit) return;
    const rate = unit.amount / 100, digits = precisionCurrencies.has(unit.currency) ? 3 : 2;
    if (!state.ready || unit.currency !== state.currency || rate !== state.rate) {
      state.input.value = state.minor === null ? '' : (state.minor / 100 * rate).toFixed(digits);
      state.lastValue = state.input.value;
      state.input.step = String(10 ** -digits);
      const placeholder = Number(field.dataset.rdbBudgetPlaceholder || 0);
      if (Number.isSafeInteger(placeholder) && placeholder >= 0) state.input.placeholder = String(Number((placeholder / 100 * rate).toFixed(digits)));
      setText(field.querySelector('[data-rdb-budget-field-currency]'), unit.currency);
      Object.assign(state, { currency: unit.currency, rate, ready: true });
    }
  }

  function render(element, code) {
    if (element.hasAttribute('data-rdb-budget-field')) { renderBudgetField(element, code); return; }
    if (element.matches(budgetSelector)) { renderBudgetText(element, code); return; }
    if (element.hasAttribute('data-rdb-price')) {
      const base = element.dataset.rdbBaseCurrency;
      const value = quote(element.dataset.rdbPrice, base, code);
      if (!value) return;
      setText(element.querySelector('[data-rdb-price-amount]'), number(value.amount, value.currency, value.currency === base && element.hasAttribute('data-rdb-trim-base-decimals')));
      setText(element.querySelector('[data-rdb-price-currency]'), value.currency);
      return;
    }
    if (!precisionCurrencies.has(code)) return;
    const amount = Number(element.getAttribute('current_price'));
    if (!Number.isFinite(amount)) return;
    const textNodes = [...element.childNodes].filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNodes.length !== 1) return;
    const value = `${number(amount, code, false)} ${code}`;
    if (textNodes[0].data !== value) textNodes[0].data = value;
    element.dir = 'ltr';
    element.style.unicodeBidi = 'isolate';
  }

  function flush() {
    frame = 0;
    const code = currency();
    const changed = code !== previousCurrency;
    if (scanAll || code !== previousCurrency) document.querySelectorAll(targetSelector).forEach(element => pending.add(element));
    scanAll = false;
    previousCurrency = code;
    pending.forEach(element => { if (element.isConnected) render(element, code); });
    pending.clear();
    if (changed) document.dispatchEvent(new CustomEvent('rdb:currencychange', { detail: { currency: code } }));
  }

  function schedule(all = false) {
    scanAll ||= all;
    if (!frame) frame = requestAnimationFrame(flush);
  }

  function collect(node) {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!element) return;
    const owner = element.closest(targetSelector);
    if (owner) pending.add(owner);
    if (element.matches('rdb-currency') || element.closest('rdb-currency')) scanAll = true;
    element.querySelectorAll?.(targetSelector).forEach(price => pending.add(price));
  }

  function start() {
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'childList') {
          collect(record.target);
          record.addedNodes.forEach(collect);
        } else collect(record.target);
      });
      if (pending.size || scanAll) schedule();
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['data-rdb-price', 'current_price'] });
    document.addEventListener('futureblink:currencychange', () => schedule(true));
    schedule(true);
  }

  window.rdbCurrencyPrices = {
    refresh: () => schedule(true),
    format: (minor, base = 'AED') => {
      const value = quote(minor, base);
      return value ? `${value.currency} ${number(value.amount, value.currency, false)}` : null;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
